import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { motion, AnimatePresence, useMotionValue, animate } from 'framer-motion';
import { Box, useTheme } from '@mui/material';
import AutoExpandedView from '../expanded/AutoExpandedView';

interface AnimatedExpandedContentProps<T, U> {
  row: T;
  index: number;
  isOpen: boolean;
  lockedIndex: number | null;
  tableRef: React.RefObject<HTMLElement> | undefined;
  rowRefs: React.MutableRefObject<(HTMLDivElement | null)[]>;
  lastLockedVisualTopRef: React.MutableRefObject<number>;
  enterWaitIndexRef: React.MutableRefObject<number | null>;
  exitWaitIndexRef: React.MutableRefObject<number | null>;
  setLockedIndex: (index: number | null) => void;
  getChildRows: (row: T, childRowsKey?: keyof T) => T[] | U[];
  childRowsKey?: keyof T;
  renderExpanded?: (row: T, childRow: U | undefined, childIndex: number, mobileView: boolean) => React.ReactNode;
  columns: any[];
  regularActions: any[];
  panelLayout?: boolean;
}

const AnimatedExpandedContent = <T, U>({
  row,
  index,
  isOpen,
  lockedIndex,
  tableRef,
  rowRefs,
  lastLockedVisualTopRef,
  enterWaitIndexRef,
  exitWaitIndexRef,
  setLockedIndex,
  getChildRows,
  childRowsKey,
  renderExpanded,
  columns,
  regularActions,
  panelLayout
}: AnimatedExpandedContentProps<T, U>) => {
  const theme = useTheme();
  const heightValue = useMotionValue(0);
  const opacityValue = useMotionValue(0);

  const contentRef = useRef<HTMLDivElement | null>(null);
  const [measuredHeight, setMeasuredHeight] = useState<number>(0);

  // Measure content height so we can animate 0 -> px (avoid height: auto flicker)
  useLayoutEffect(() => {
    if (!isOpen) return;
    const el = contentRef.current;
    if (!el) return;

    // rAF to ensure DOM is painted with children before measuring
    requestAnimationFrame(() => {
      const next = el.scrollHeight;
      setMeasuredHeight(next);
    });
  }, [isOpen, row, childRowsKey, renderExpanded, columns, regularActions]);

  useEffect(() => {
    if (isOpen) {
      // אנימציה מותאמת אישית לפתיחה
      const heightControls = animate(heightValue, 1, {
        duration: 0.25,
        ease: 'easeInOut',
        onUpdate: () => {
          // תיקון רציף במהלך האנימציה
          if (lockedIndex === index) {
            const containerEl = tableRef?.current as HTMLElement | null;
            const rowEl = rowRefs.current[index];

            if (containerEl && rowEl) {
              const desiredTop = lastLockedVisualTopRef.current;
              const currentTop = rowEl.getBoundingClientRect().top;
              const delta = currentTop - desiredTop;

              // תיקון חלק - רק 20% מההפרש בכל frame
              if (Math.abs(delta) > 0.1) {
                containerEl.scrollTop = containerEl.scrollTop + delta * 0.2;
              }
            }
          }
        },
        onComplete: () => {
          // סיום האנימציה
          if (enterWaitIndexRef.current === index) enterWaitIndexRef.current = null;
          if (exitWaitIndexRef.current === index) exitWaitIndexRef.current = null;

          if (enterWaitIndexRef.current === null && exitWaitIndexRef.current === null) {
            const containerEl = tableRef?.current as HTMLElement | null;
            const rowEl = rowRefs.current[index];

            if (containerEl && rowEl) {
              // תיקון סופי מדויק
              requestAnimationFrame(() => {
                const desiredTop = lastLockedVisualTopRef.current;
                const currentTop = rowEl.getBoundingClientRect().top;
                const delta = currentTop - desiredTop;

                if (Math.abs(delta) > 0.25) {
                  containerEl.scrollTop = containerEl.scrollTop + delta;
                }

                setLockedIndex(null);
                requestAnimationFrame(() => {
                  delete (containerEl as any).dataset.suppressCollapse;
                });
              });
            } else {
              setLockedIndex(null);
            }
          }
        }
      });

      // אנימציית שקיפות מקבילה
      const opacityControls = animate(opacityValue, 1, {
        duration: 0.25,
        ease: 'easeInOut'
      });

      return () => {
        heightControls.stop();
        opacityControls.stop();
        setLockedIndex(null);
      };
    } else {
      // אנימציית סגירה
      const heightControls = animate(heightValue, 0, {
        duration: 0.25,
        ease: 'easeInOut'
      });

      const opacityControls = animate(opacityValue, 0, {
        duration: 0.25,
        ease: 'easeInOut'
      });

      return () => {
        heightControls.stop();
        opacityControls.stop();
        setLockedIndex(null);
      };
    }
  }, [
    isOpen,
    index,
    heightValue,
    opacityValue,
    lockedIndex,
    tableRef,
    rowRefs,
    lastLockedVisualTopRef,
    enterWaitIndexRef,
    exitWaitIndexRef,
    setLockedIndex
  ]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key={index}
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: measuredHeight, opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.22, ease: 'easeInOut' }}
          style={{ overflow: 'hidden' }}
        >
          <Box ref={contentRef}>
            {getChildRows(row, childRowsKey)?.map((childRow, childIndex) => (
              <Box
                key={childIndex}
                sx={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  p: 0,
                  pt: childIndex === 0 ? '5px' : 2,
                  ...(getChildRows(row, childRowsKey)?.length > 1 &&
                    childIndex !== getChildRows(row, childRowsKey)?.length - 1 && {
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      mb: 2
                    })
                }}
              >
                {renderExpanded ? (
                  <Box
                    sx={{
                      width: '100%',
                      borderRadius: '10px',
                      overflow: 'hidden',
                      bgcolor: theme.palette.grey[600],
                      py: 3,
                      px: 2
                    }}
                  >
                    {renderExpanded(row, childRowsKey ? (childRow as U) : undefined, childIndex, true)}
                  </Box>
                ) : (
                  <AutoExpandedView
                    row={row}
                    columns={columns}
                    actions={regularActions}
                    childRow={childRow}
                    childIndex={childIndex}
                    panelLayout={panelLayout}
                  />
                )}
              </Box>
            ))}
          </Box>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AnimatedExpandedContent;
